/**
 * Keep in sync with
 * nuxino_back_mirco/nuxino_app/src/app/payment-gateways/source-policy/gateway-source-policy.ts
 *
 * Active source channel: cookie `payment_gateway_session` (max-age 3600) stores the
 * gateway code. The same string is sent as GraphQL `source` on getPaymentGateways
 * and as OrderPayInput.source on pay.
 */

export type GatewayVisibility = "public" | "source_only";

export type GatewaySourcePolicy = {
  visibility: GatewayVisibility;
  detectEnabled: boolean;
  matchKeys: string[];
  restrictionEnabled: boolean;
  allowSelf: boolean;
  allowNonInstallment: boolean;
  allowOtherInstallment: boolean;
  allowGatewayCodes: string[] | null;
  denyGatewayCodes: string[] | null;
  sourcePriority?: number | null;
};

export type GatewaySourceView = {
  code: string;
  name?: string;
  installment: boolean;
  sourcePolicy?: GatewaySourcePolicy | null;
};

export type PaymentSourceDetector = {
  code: string;
  matchKeys: string[];
  sourcePriority: number;
};

export const DEFAULT_SOURCE_PRIORITY = 100;

export const EMPTY_SOURCE_POLICY: GatewaySourcePolicy = {
  visibility: "public",
  detectEnabled: false,
  matchKeys: [],
  restrictionEnabled: false,
  allowSelf: true,
  allowNonInstallment: true,
  allowOtherInstallment: true,
  allowGatewayCodes: null,
  denyGatewayCodes: null,
  sourcePriority: null,
};

export function coerceSourcePolicy(raw: unknown): GatewaySourcePolicy | null {
  if (!raw || typeof raw !== "object") return null;
  const policy = raw as Partial<GatewaySourcePolicy>;
  return {
    visibility: policy.visibility === "source_only" ? "source_only" : "public",
    detectEnabled: Boolean(policy.detectEnabled),
    matchKeys: uniqueTrimmed(policy.matchKeys),
    restrictionEnabled: Boolean(policy.restrictionEnabled),
    allowSelf: policy.allowSelf !== false,
    allowNonInstallment: policy.allowNonInstallment !== false,
    allowOtherInstallment: policy.allowOtherInstallment !== false,
    allowGatewayCodes: codesOrNull(policy.allowGatewayCodes),
    denyGatewayCodes: codesOrNull(policy.denyGatewayCodes),
    sourcePriority: typeof policy.sourcePriority === "number" ? policy.sourcePriority : null,
  };
}

export function isGatewayVisibleForSource(
  candidate: GatewaySourceView,
  activeSourceCode: string | null | undefined,
  gateways: GatewaySourceView[],
): boolean {
  const source = canonicalSourceCode(activeSourceCode, gateways);
  const candidatePolicy = readPolicy(candidate.sourcePolicy);
  if (candidatePolicy.visibility === "source_only" && source !== candidate.code) {
    return false;
  }
  if (!source) return true;
  const owner = gateways.find((gateway) => gateway.code === source);
  if (!owner) return true;
  const ownerPolicy = readPolicy(owner.sourcePolicy);
  if (!ownerPolicy.restrictionEnabled) return true;
  return passesRestriction(candidate, owner.code, ownerPolicy);
}

export function filterGatewaysForSource<T extends GatewaySourceView>(
  gateways: T[],
  activeSourceCode: string | null | undefined,
): T[] {
  return gateways.filter((gateway) => isGatewayVisibleForSource(gateway, activeSourceCode, gateways));
}

export function resolvePaymentSource(
  detectors: PaymentSourceDetector[],
  signals: {
    utmSource?: string | null;
    source?: string | null;
    referrer?: string | null;
  },
): string | null {
  const host = referrerHostname(signals.referrer);
  const texts = [signals.utmSource, signals.source].map((value) => value?.trim().toLowerCase() ?? "").filter(Boolean);

  const matched = detectors.filter((detector) => {
    const keys = (detector.matchKeys ?? []).map((key) => key.trim().toLowerCase()).filter(Boolean);
    return keys.some((key) => texts.some((text) => text.includes(key)) || (host.length > 0 && host.includes(key)));
  });
  if (!matched.length) return null;

  matched.sort((a, b) => {
    const priorityA = a.sourcePriority ?? DEFAULT_SOURCE_PRIORITY;
    const priorityB = b.sourcePriority ?? DEFAULT_SOURCE_PRIORITY;
    if (priorityA !== priorityB) return priorityA - priorityB;
    return a.code.localeCompare(b.code);
  });
  return matched[0].code;
}

export function suggestedMatchKeys(code: string): string[] {
  if (code === "snappay") return ["snapp"];
  if (code === "apoloan") return ["apoloan"];
  if (code === "grand-bazaar") return ["grandbazaar", "grand-bazaar", "grand bazaar"];
  return [];
}

export function presetExclusiveWithCash(code: string, currentKeys: string[]): GatewaySourcePolicy {
  return {
    visibility: "public",
    detectEnabled: true,
    matchKeys: currentKeys.length ? currentKeys : suggestedMatchKeys(code),
    restrictionEnabled: true,
    allowSelf: true,
    allowNonInstallment: true,
    allowOtherInstallment: false,
    allowGatewayCodes: null,
    denyGatewayCodes: null,
    sourcePriority: 10,
  };
}

export function presetDetectOnly(code: string, currentKeys: string[]): GatewaySourcePolicy {
  return {
    visibility: "public",
    detectEnabled: true,
    matchKeys: currentKeys.length ? currentKeys : suggestedMatchKeys(code),
    restrictionEnabled: false,
    allowSelf: true,
    allowNonInstallment: true,
    allowOtherInstallment: true,
    allowGatewayCodes: null,
    denyGatewayCodes: null,
    sourcePriority: 50,
  };
}

export function presetPartnerOnly(code: string, currentKeys: string[]): GatewaySourcePolicy {
  return {
    visibility: "source_only",
    detectEnabled: true,
    matchKeys: currentKeys.length ? currentKeys : suggestedMatchKeys(code),
    restrictionEnabled: true,
    allowSelf: true,
    allowNonInstallment: false,
    allowOtherInstallment: false,
    allowGatewayCodes: null,
    denyGatewayCodes: null,
    sourcePriority: 10,
  };
}

export type AppliedPolicyLine = { label: string; text: string };

export function appliedPolicySummary(input: {
  policy: GatewaySourcePolicy | null;
  name: string;
  code: string;
  directList: GatewaySourceView[];
  selfList: GatewaySourceView[];
  knownGateways: { code: string; name?: string }[];
}): AppliedPolicyLine[] {
  const { policy, name, code, directList, selfList, knownGateways } = input;
  const labelOf = (gatewayCode: string) => {
    if (gatewayCode === code) return name || code;
    return knownGateways.find((gateway) => gateway.code === gatewayCode)?.name || gatewayCode;
  };
  const listText = (list: GatewaySourceView[]) => {
    if (!list.length) return "هیچ روش پرداختی";
    return list.map((gateway) => gateway.name || gateway.code).join("، ");
  };

  if (!policy) {
    return [
      {
        label: "نمایش این درگاه",
        text: `«${name}» برای کسی که فروشگاه را مستقیم باز می‌کند هم نشان داده می‌شود، به شرطی که خود درگاه روشن باشد.`,
      },
      {
        label: "لینک ورود",
        text: "لینک ورود بررسی نمی‌شود. برای این درگاه فرقی نمی‌کند مشتری از کجا آمده باشد.",
      },
      {
        label: "فهرست پرداخت",
        text: "فهرست پرداخت دست نمی‌خورد. مشتری همان روش‌های همیشگی را می‌بیند.",
      },
      {
        label: "در ورود عادی می‌ماند",
        text: listText(directList),
      },
    ];
  }

  const priority = typeof policy.sourcePriority === "number" ? policy.sourcePriority : DEFAULT_SOURCE_PRIORITY;
  const keys = policy.matchKeys.map((key) => key.trim()).filter(Boolean);
  const visibility =
    policy.visibility === "source_only"
      ? `«${name}» در ورود عادی دیده نمی‌شود. فقط کسی آن را می‌بیند که ورودش مال همین درگاه شناخته شده باشد.`
      : `«${name}» برای کسی که فروشگاه را مستقیم باز می‌کند هم نشان داده می‌شود، به شرطی که خود درگاه روشن باشد و مبلغ سفارش در محدوده همین درگاه جا شود.`;

  let detection: string;
  if (!policy.detectEnabled) {
    detection = "لینک ورود بررسی نمی‌شود، پس خودِ این درگاه ورود مشتری را به نام خودش ثبت نمی‌کند.";
  } else if (!keys.length) {
    detection = "شناختن لینک روشن است، اما هنوز عبارتی ننوشته‌ای. تا وقتی عبارت نباشد، هیچ ورودی مال این درگاه حساب نمی‌شود.";
  } else {
    detection = `اگر لینک مشتری یا آدرس سایت قبلی‌اش شامل ${keys.map((key) => `«${key}»`).join(" یا ")} باشد، ورودش تا یک ساعت مال «${name}» می‌ماند. اگر همان لینک با درگاه دیگری هم جور شود، عددی که کوچک‌تر است می‌ماند. عدد این درگاه ${priority} است.`;
  }

  const denied = (policy.denyGatewayCodes ?? []).filter(Boolean);
  const denyText = denied.length ? ` این روش‌ها در هر صورت حذف می‌شوند: ${denied.map(labelOf).join("، ")}.` : "";

  let restriction: string;
  if (!policy.restrictionEnabled) {
    restriction = `کسی که ورودش مال «${name}» باشد همان روش‌های ورود عادی را می‌بیند${
      policy.visibility === "source_only" ? "، به‌علاوه خود این درگاه" : ""
    }.`;
  } else if (policy.allowGatewayCodes?.length) {
    const allowed = policy.allowGatewayCodes.filter((item) => !denied.includes(item));
    restriction = allowed.length
      ? `برای این مشتری‌ها فقط روش‌هایی می‌ماند که دستی انتخاب شده‌اند: ${allowed.map(labelOf).join("، ")}.${denyText}`
      : `برای این مشتری‌ها هیچ روش پرداختی نمی‌ماند.${denyText}`;
  } else {
    const kept = [
      policy.allowSelf && !denied.includes(code) ? "همین درگاه" : null,
      policy.allowNonInstallment ? "پرداخت نقدی" : null,
      policy.allowOtherInstallment ? "قسط‌های دیگر" : null,
    ].filter(Boolean);
    restriction = kept.length
      ? `برای این مشتری‌ها فقط این‌ها می‌ماند: ${kept.join("، ")}.${denyText}`
      : `برای این مشتری‌ها هیچ روش پرداختی نمی‌ماند.${denyText}`;
  }

  return [
    { label: "نمایش این درگاه", text: visibility },
    { label: "لینک ورود", text: detection },
    { label: "اگر ورود مال این درگاه باشد", text: `${restriction} در عمل می‌بیند: ${listText(selfList)}.` },
    { label: "در ورود عادی می‌ماند", text: listText(directList) },
  ];
}

export function sourcePolicySaveWarnings(policy: GatewaySourcePolicy | null, code: string): string[] {
  if (!policy) return [];
  const warnings: string[] = [];
  const keys = policy.matchKeys.map((key) => key.trim()).filter(Boolean);
  const shortKeys = keys.filter((key) => key.length < 3);

  if (policy.visibility === "source_only" && (!policy.detectEnabled || keys.length === 0)) {
    warnings.push("درگاه فقط از لینک خودش دیده می‌شود، ولی آن لینک شناخته نمی‌شود. مشتری هیچ‌وقت این درگاه را نمی‌بیند.");
  } else if (policy.detectEnabled && keys.length === 0) {
    warnings.push("شناختن لینک روشن است، ولی عبارتی ننوشته‌ای. هیچ ورودی شناخته نمی‌شود.");
  }
  if (shortKeys.length) {
    warnings.push(`عبارت‌های کوتاه (${shortKeys.join("، ")}) ممکن است لینک‌های بی‌ربط را هم بگیرند.`);
  }
  if (policy.restrictionEnabled && !selfIsAllowed(policy, code) && !restrictionAllowsNothing(policy, code)) {
    warnings.push("فهرست محدود شده، ولی خود این درگاه حذف شده. مشتری از این لینک نمی‌تواند با همین درگاه پرداخت کند.");
  }
  if (policy.restrictionEnabled && restrictionAllowsNothing(policy, code)) {
    warnings.push("فهرست محدود شده، ولی هیچ روشی نمی‌ماند. پرداخت از این لینک بسته می‌شود.");
  }
  return warnings;
}

export function describeSourceOutcomes(
  self: GatewaySourceView,
  others: GatewaySourceView[],
): {
  direct: string;
  fromSelf: string;
  fromOther: string;
  otherLabel: string;
  otherCode: string;
  otherIsConfigured: boolean;
  directList: GatewaySourceView[];
  selfList: GatewaySourceView[];
  otherList: GatewaySourceView[];
} {
  const catalog = [self, ...others.filter((gateway) => gateway.code !== self.code)];
  const other =
    others.find((gateway) => gateway.code !== self.code && gateway.sourcePolicy?.restrictionEnabled) ??
    ({
      code: "__other_partner__",
      name: "شریک انحصاری دیگر",
      installment: true,
      sourcePolicy: presetPartnerOnly("__other_partner__", ["other"]),
    } satisfies GatewaySourceView);

  const withOther = catalog.some((gateway) => gateway.code === other.code) ? catalog : [...catalog, other];
  const directList = filterGatewaysForSource(catalog, null);
  const selfList = filterGatewaysForSource(catalog, self.code);
  const otherList = filterGatewaysForSource(withOther, other.code);

  return {
    direct: `بازدید مستقیم → ${visibleSentence(directList, self.code)}`,
    fromSelf: `کاربر از این منبع → ${visibleSentence(selfList, self.code)}`,
    fromOther: `کاربر از ${other.name || other.code} → ${visibleSentence(otherList, self.code)}`,
    otherLabel: other.name || other.code,
    otherCode: other.code,
    otherIsConfigured: other.code !== "__other_partner__",
    directList,
    selfList,
    otherList,
  };
}

function selfIsAllowed(policy: GatewaySourcePolicy, code: string): boolean {
  if (!policy.restrictionEnabled) return true;
  if (policy.denyGatewayCodes?.includes(code)) return false;
  if (policy.allowGatewayCodes?.length) return policy.allowGatewayCodes.includes(code);
  return policy.allowSelf;
}

function restrictionAllowsNothing(policy: GatewaySourcePolicy, code: string): boolean {
  if (!policy.restrictionEnabled) return false;
  const denied = new Set(policy.denyGatewayCodes ?? []);
  if (policy.allowGatewayCodes?.length) {
    return policy.allowGatewayCodes.every((item) => denied.has(item));
  }
  const flags = [
    policy.allowSelf && !denied.has(code),
    policy.allowNonInstallment,
    policy.allowOtherInstallment,
  ];
  return flags.every((allowed) => !allowed);
}

function visibleSentence(visible: GatewaySourceView[], selfCode: string): string {
  if (!visible.length) return "هیچ روش پرداختی نشان داده نمی‌شود.";
  const names = visible.map((gateway) => {
    const label = gateway.name || gateway.code;
    return gateway.code === selfCode ? `${label} (این درگاه)` : label;
  });
  const includesSelf = visible.some((gateway) => gateway.code === selfCode);
  if (!includesSelf) return `این درگاه مخفی است. مشتری می‌بیند: ${names.join("، ")}.`;
  return `مشتری می‌بیند: ${names.join("، ")}.`;
}

type ResolvedPolicy = GatewaySourcePolicy & { sourcePriority: number };

function readPolicy(policy: GatewaySourcePolicy | null | undefined): ResolvedPolicy {
  if (!policy) {
    return {
      ...EMPTY_SOURCE_POLICY,
      allowSelf: true,
      allowNonInstallment: true,
      allowOtherInstallment: true,
      sourcePriority: DEFAULT_SOURCE_PRIORITY,
    };
  }
  return {
    visibility: policy.visibility === "source_only" ? "source_only" : "public",
    detectEnabled: Boolean(policy.detectEnabled),
    matchKeys: uniqueTrimmed(policy.matchKeys),
    restrictionEnabled: Boolean(policy.restrictionEnabled),
    allowSelf: policy.allowSelf !== false,
    allowNonInstallment: policy.allowNonInstallment !== false,
    allowOtherInstallment: policy.allowOtherInstallment !== false,
    allowGatewayCodes: codesOrNull(policy.allowGatewayCodes),
    denyGatewayCodes: codesOrNull(policy.denyGatewayCodes),
    sourcePriority: typeof policy.sourcePriority === "number" ? policy.sourcePriority : DEFAULT_SOURCE_PRIORITY,
  };
}

function passesRestriction(candidate: GatewaySourceView, ownerCode: string, policy: ResolvedPolicy): boolean {
  const allowList = policy.allowGatewayCodes ?? [];
  const denyList = policy.denyGatewayCodes ?? [];
  if (allowList.length) {
    if (!allowList.includes(candidate.code)) return false;
  } else if (candidate.code === ownerCode) {
    if (!policy.allowSelf) return false;
  } else if (!candidate.installment) {
    if (!policy.allowNonInstallment) return false;
  } else if (!policy.allowOtherInstallment) {
    return false;
  }
  if (denyList.includes(candidate.code)) return false;
  return true;
}

function canonicalSourceCode(source: string | null | undefined, gateways: { code: string }[]): string | null {
  const trimmed = source?.trim();
  if (!trimmed) return null;
  const found = gateways.find((gateway) => gateway.code.toLowerCase() === trimmed.toLowerCase());
  return found ? found.code : trimmed;
}

function codesOrNull(values?: string[] | null): string[] | null {
  const codes = uniqueTrimmed(values);
  return codes.length ? codes : null;
}

function uniqueTrimmed(values?: string[] | null): string[] {
  const result: string[] = [];
  const seen = new Set<string>();
  for (const value of values ?? []) {
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(trimmed);
  }
  return result;
}

function referrerHostname(referrer?: string | null): string {
  if (!referrer?.trim()) return "";
  try {
    return new URL(referrer).hostname.toLowerCase();
  } catch {
    return "";
  }
}
